import { useCallback, useMemo, useState } from "react"
import { useAudienceClassificationControllerGetAllQuery } from "~/ui-rtk/api/audienceClassificationApi"
import {
  useCustomAttributionControllerGetAllMediaTargetsQuery,
  useCustomAttributionControllerClassifyCampaignsMutation,
  useCustomAttributionControllerBulkCreateMediaTargetsMutation,
} from "~/ui-rtk/api/customAttributionApi"

// ── Types ──────────────────────────────────────────────────────────────

type AdsetRow = Record<string, unknown>

interface ClassificationResult {
  campaign_id: string
  adset_id: string
  initiativeType: string
  media_channel: string
}

// ── Pure classification helpers ────────────────────────────────────────

const META_CONVERSION_OBJECTIVES = new Set([
  "OUTCOME_SALES",
  "OUTCOME_LEADS",
  "CONVERSIONS",
  "PRODUCT_CATALOG_SALES",
])

const META_NON_CONVERSION_OBJECTIVES = new Set([
  "OUTCOME_ENGAGEMENT",
  "OUTCOME_TRAFFIC",
  "OUTCOME_AWARENESS",
  "REACH",
  "BRAND_AWARENESS",
  "LINK_CLICKS",
])

const TIKTOK_NON_CONVERSION_OBJECTIVES = new Set([
  "REACH",
  "TRAFFIC",
  "VIDEO_VIEWS",
  "ENGAGEMENT",
  "FOLLOWERS",
  "PROFILE_VIEWS",
  "VIDEO_VIEW",
  "MT_LIVE_ROOM",
  "PAGE_VISIT",
  "TRAFFIC_LANDING_PAGE_VIEW",
])

// Pinterest ad groups carry no campaign objective (NULL, TikTok convention);
// pipeline-dbt derives `optimization_goal` from the conversion event
// (CHECKOUT, ADD_TO_CART, SIGNUP, LEAD, PAGE_VISIT) for conversion ad groups,
// else the billable event. Billable-event goals and page visits are demand
// generation; the remaining conversion goals need audience data to pick a
// demand-capture subtype, so they stay unclassified.
const PINTEREST_NON_CONVERSION_GOALS = new Set([
  "IMPRESSION",
  "CLICKTHROUGH",
  "VIDEO_V_50_MRC",
  "PAGE_VISIT",
])

const QP = "company_media_metrics_adsets"

function parseJsonArray(raw: unknown): { id: string; name: string }[] {
  if (!raw || typeof raw !== "string") return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.map((item: { id?: string; name?: string }) => ({
      id: String(item.id ?? ""),
      name: String(item.name ?? ""),
    }))
  } catch {
    return []
  }
}

function getAdChannelType(mediaChannel: string): string {
  const ch = mediaChannel.toLowerCase()
  if (ch === "meta") return "Meta"
  if (ch === "tiktok") return "Tiktok"
  if (ch === "pinterest") return "Pinterest"
  if (ch === "google_ads") return "Google Ads"
  if (ch === "youtube_ads") return "YouTube"
  return mediaChannel
}

export function isConversionObjective(
  channel: string,
  objective: string,
): boolean {
  const ch = channel.toLowerCase()
  if (ch === "meta") return META_CONVERSION_OBJECTIVES.has(objective)
  if (ch === "tiktok") {
    return !TIKTOK_NON_CONVERSION_OBJECTIVES.has(objective)
  }
  if (ch === "pinterest") {
    return !PINTEREST_NON_CONVERSION_GOALS.has(objective)
  }
  return false
}

/**
 * Classify a Meta adset based on audience targeting and objectives.
 * Returns the initiative type or null if unclassifiable.
 */
export function classifyMetaAdset(
  row: AdsetRow,
  engagedIds: Set<string>,
  existingIds: Set<string>,
): string | null {
  const objective = String(row[`${QP}.objective`] ?? "")
  const targetedAudiences = parseJsonArray(
    row[`${QP}.targeting_custom_audiences`],
  )
  const excludedAudiences = parseJsonArray(
    row[`${QP}.targeting_excluded_custom_audiences`],
  )

  const targetedIds = new Set(targetedAudiences.map(a => a.id))
  const excludedIds = new Set(excludedAudiences.map(a => a.id))

  const targetsEngaged = [...engagedIds].some(id => targetedIds.has(id))
  const targetsExisting = [...existingIds].some(id => targetedIds.has(id))
  const excludesEngaged = [...engagedIds].some(id => excludedIds.has(id))
  const excludesExisting = [...existingIds].some(id => excludedIds.has(id))

  // 1. Demand Capture Retargeting — targeting engaged audiences
  if (targetsEngaged) return "DEMAND_CAPTURE_RETARGETING"

  // 2. Retention — targeting existing customers (but not engaged)
  if (targetsExisting) return "RETENTION"

  const isConversion = META_CONVERSION_OBJECTIVES.has(objective)
  const isNonConversion = META_NON_CONVERSION_OBJECTIVES.has(objective)
  const isBroadAudience = targetedAudiences.length === 0

  // 3. Demand Capture Acquisition — conversion + excluding both engaged & existing + NOT targeting them
  if (
    isConversion &&
    excludesEngaged &&
    excludesExisting &&
    !targetsEngaged &&
    !targetsExisting
  ) {
    return "DEMAND_CAPTURE_ACQUISITION"
  }

  // 4. Demand Capture Broad — broad audience + NOT excluding engaged
  if (isConversion && isBroadAudience && !excludesEngaged) {
    return "DEMAND_CAPTURE_BROAD"
  }

  // 5. Demand Generation — non-conversion objective + (broad or new customer)
  if (
    isNonConversion &&
    (isBroadAudience || (!targetsEngaged && !targetsExisting))
  ) {
    return "DEMAND_GENERATION"
  }

  return null // Unclassified
}

/**
 * Classify a TikTok adset based on objectives only.
 */
export function classifyTiktokAdset(row: AdsetRow): string | null {
  const objective = String(row[`${QP}.objective`] ?? "")

  if (TIKTOK_NON_CONVERSION_OBJECTIVES.has(objective)) {
    return "DEMAND_GENERATION"
  }

  // Conversion objective but can't determine subtype without audience data
  return null
}

/**
 * Classify a Pinterest ad group from its optimization goal (campaign objective
 * is NULL for Pinterest rows).
 */
export function classifyPinterestAdset(row: AdsetRow): string | null {
  const goal = String(row[`${QP}.optimization_goal`] ?? "")

  if (PINTEREST_NON_CONVERSION_GOALS.has(goal)) {
    return "DEMAND_GENERATION"
  }

  // Conversion goal but can't determine subtype without audience data
  return null
}

// ── Main hook ──────────────────────────────────────────────────────────

export function useAutoClassify() {
  const [isClassifying, setIsClassifying] = useState(false)
  const [results, setResults] = useState<ClassificationResult[]>([])

  const { data: classifications } =
    useAudienceClassificationControllerGetAllQuery()

  const { data: allMediaTargets = [] } =
    useCustomAttributionControllerGetAllMediaTargetsQuery()

  const [classifyCampaigns] =
    useCustomAttributionControllerClassifyCampaignsMutation()

  const [bulkCreateMediaTargets] =
    useCustomAttributionControllerBulkCreateMediaTargetsMutation()

  // Audience IDs from classifications
  const { engagedIds, existingIds } = useMemo(() => {
    if (!classifications) {
      return { engagedIds: new Set<string>(), existingIds: new Set<string>() }
    }
    return {
      engagedIds: new Set(
        classifications
          .filter(c => c.classification === "ENGAGED")
          .map(c => c.audienceRef),
      ),
      existingIds: new Set(
        classifications
          .filter(c => c.classification === "EXISTING")
          .map(c => c.audienceRef),
      ),
    }
  }, [classifications])

  // Set of already-assigned adset keys
  const assignedAdsetKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const target of allMediaTargets as Array<{
      campaign_id?: string
      ad_set_id?: string
    }>) {
      if (target.ad_set_id) {
        keys.add(target.ad_set_id)
      }
    }
    return keys
  }, [allMediaTargets])

  // Check if there are unassigned adsets in given data
  const hasUnassignedAdsets = useCallback(
    (adsetData: AdsetRow[]) =>
      adsetData.some(row => {
        const adsetId = String(row[`${QP}.adset_id`] ?? "")
        return adsetId && !assignedAdsetKeys.has(adsetId)
      }),
    [assignedAdsetKeys],
  )

  const classify = useCallback(
    async (adsetData: AdsetRow[]) => {
      setIsClassifying(true)
      setResults([])

      try {
        // Filter to unassigned adsets only
        const unassigned = adsetData.filter(row => {
          const adsetId = String(row[`${QP}.adset_id`] ?? "")
          return adsetId && !assignedAdsetKeys.has(adsetId)
        })

        if (unassigned.length === 0) {
          return []
        }

        const classified: ClassificationResult[] = []
        const googleYoutubeCampaigns: Array<{
          campaign_id: string
          campaign_name: string
          media_channel: string
          optimization_goal: string
          adset_id: string
        }> = []

        // 1. Rule-based classification for Meta, TikTok and Pinterest
        for (const row of unassigned) {
          const mediaChannel = String(row[`${QP}.media_channel`] ?? "")
          const adsetId = String(row[`${QP}.adset_id`] ?? "")
          const campaignId = String(row[`${QP}.campaign_id`] ?? "")
          const ch = mediaChannel.toLowerCase()

          if (ch === "meta") {
            const result = classifyMetaAdset(row, engagedIds, existingIds)
            if (result) {
              classified.push({
                campaign_id: campaignId,
                adset_id: adsetId,
                initiativeType: result,
                media_channel: mediaChannel,
              })
            }
          } else if (ch === "tiktok") {
            const result = classifyTiktokAdset(row)
            if (result) {
              classified.push({
                campaign_id: campaignId,
                adset_id: adsetId,
                initiativeType: result,
                media_channel: mediaChannel,
              })
            }
          } else if (ch === "pinterest") {
            const result = classifyPinterestAdset(row)
            if (result) {
              classified.push({
                campaign_id: campaignId,
                adset_id: adsetId,
                initiativeType: result,
                media_channel: mediaChannel,
              })
            }
          } else if (ch === "google_ads" || ch === "youtube_ads") {
            googleYoutubeCampaigns.push({
              campaign_id: campaignId,
              campaign_name: String(row[`${QP}.campaign_name`] ?? ""),
              media_channel: mediaChannel,
              optimization_goal: String(row[`${QP}.optimization_goal`] ?? ""),
              adset_id: adsetId,
            })
          }
        }

        // 2. LLM classification for Google/YouTube campaigns
        if (googleYoutubeCampaigns.length > 0) {
          // Dedupe by campaign_id for the LLM call, skip campaigns with no name
          const uniqueCampaigns = new Map<
            string,
            (typeof googleYoutubeCampaigns)[0]
          >()
          for (const c of googleYoutubeCampaigns) {
            if (!uniqueCampaigns.has(c.campaign_id) && c.campaign_name) {
              uniqueCampaigns.set(c.campaign_id, c)
            }
          }

          if (uniqueCampaigns.size > 0) {
            try {
              const response = await classifyCampaigns({
                campaigns: Array.from(uniqueCampaigns.values()).map(c => ({
                  campaign_id: c.campaign_id,
                  campaign_name: c.campaign_name,
                  media_channel: c.media_channel,
                  optimization_goal: c.optimization_goal,
                })),
              }).unwrap()

              // Map LLM results back to adsets
              const campaignClassMap = new Map<string, string>()
              for (const c of response.classifications) {
                if (c.initiative_type !== "UNCLASSIFIED") {
                  campaignClassMap.set(c.campaign_id, c.initiative_type)
                }
              }

              for (const adset of googleYoutubeCampaigns) {
                const initiativeType = campaignClassMap.get(adset.campaign_id)
                if (initiativeType) {
                  classified.push({
                    campaign_id: adset.campaign_id,
                    adset_id: adset.adset_id,
                    initiativeType,
                    media_channel: adset.media_channel,
                  })
                }
              }
            } catch (error) {
              console.error(
                "Failed to classify Google/YouTube campaigns:",
                error,
              )
            }
          }
        }

        // 3. Bulk create media targets
        if (classified.length > 0) {
          try {
            await bulkCreateMediaTargets({
              targets: classified.map(c => ({
                initiativeType: c.initiativeType,
                type: getAdChannelType(c.media_channel),
                campaign_id: c.campaign_id,
                ad_set_id: c.adset_id,
              })),
            }).unwrap()
          } catch (error) {
            console.error("Failed to bulk create media targets:", error)
          }
        }

        setResults(classified)
        return classified
      } finally {
        setIsClassifying(false)
      }
    },
    [
      assignedAdsetKeys,
      engagedIds,
      existingIds,
      classifyCampaigns,
      bulkCreateMediaTargets,
    ],
  )

  return {
    classify,
    isClassifying,
    results,
    hasUnassignedAdsets,
  }
}
